import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Divider,
  Form,
  Input,
  Popconfirm,
  Select,
  Spin,
  Tag,
} from 'antd';
import axios from 'axios';
import {jwtDecode} from 'jwt-decode';
import {useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {AppConfig} from '../../../configs';
import {RQUpdateUser, RqUserLookupById} from '../../../ReactQuery/apis';
import {logoutStart} from '../../../Redux/authAction';
import {selectAuth} from '../../../Redux/selectors';

const FIELD_DEFS = {
  specialization: {label: 'Specialization', placeholder: 'e.g. Cardiologist'},
  licenseNumber: {label: 'License Number', placeholder: 'e.g. PH123456'},
  medicalname: {label: 'Medical / Store Name', placeholder: 'e.g. Apollo Pharmacy'},
  availability: {label: 'Availability', placeholder: 'e.g. 09:00 AM - 05:00 PM'},
  workExperience: {label: 'Work Experience', placeholder: 'e.g. 5 Years'},
  qualification: {label: 'Qualification', placeholder: 'e.g. MBBS, MD'},
};

const ROLE_FIELDS = {
  DOCTOR: ['specialization', 'availability', 'workExperience', 'qualification'],
  THERAPIST: ['specialization', 'availability', 'workExperience', 'qualification'],
  PHARMACIST: ['medicalname', 'licenseNumber', 'availability', 'qualification', 'workExperience'],
  RECEPTIONIST: ['availability', 'qualification', 'workExperience'],
  ACCOUNTANT: ['availability', 'qualification', 'workExperience'],
  ADMIN: ['qualification', 'workExperience'],
  PATIENT: [],
};

const PATIENT_FIELDS = ['age', 'bloodGroup', 'occupation', 'maritalStatus'];

const ROLE_CONFIG = {
  ADMIN: {label: 'Administrator', color: 'gold'},
  DOCTOR: {label: 'Doctor', color: 'blue'},
  THERAPIST: {label: 'Therapist', color: 'purple'},
  PHARMACIST: {label: 'Pharmacist', color: 'cyan'},
  RECEPTIONIST: {label: 'Receptionist', color: 'magenta'},
  ACCOUNTANT: {label: 'Accountant', color: 'green'},
  PATIENT: {label: 'Patient', color: 'default'},
};

const STATUS_COLOR = {
  ACTIVE: 'green',
  INACTIVE: 'red',
  PENDING: 'orange',
};

const GENDER_OPTIONS = [
  {value: 'male', label: 'Male'},
  {value: 'female', label: 'Female'},
  {value: 'other', label: 'Other'},
];

const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const MARITAL_STATUS_OPTIONS = ['MARRIED', 'UNMARRIED'];

const ProfileScreen = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const auth = useSelector(selectAuth);
  const {loggedIn} = useSelector(state => state.auth);
  const token = auth?.tokenData;

  let id = null;
  let role = null;

  if (token) {
    try {
      const decoded = jwtDecode(token);
      id = decoded?.employeeId;
      role = decoded?.role;
    } catch (error) {
      id = null;
      role = null;
    }
  }

  const handleLogout = () => {
    dispatch(logoutStart());
    navigate('/');
  };

  const {
    data: profileDetails,
    isLoading,
    refetch,
    error,
  } = RqUserLookupById(id, token, {
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const updateUser = RQUpdateUser({
    onSuccess: () => {
      refetch();
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    },
    onError: err => {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Update failed',
      );
    },
  });

  useEffect(() => {
    if (profileDetails) {
      form.setFieldsValue(profileDetails);
    }
  }, [profileDetails, form]);

  const extraFields = role === 'PATIENT' ? PATIENT_FIELDS : ROLE_FIELDS[role] || [];

  const onFinish = values => {
    if (!id) {
      toast.error('Missing user id, cannot update');
      return;
    }

    const commonKeys = ['fullName', 'mobileNumber', 'gender', 'address'];
    const allowedKeys = [...commonKeys, ...extraFields];

    const formData = allowedKeys.reduce((acc, key) => {
      if (values[key] !== undefined) {
        acc[key] = values[key];
      }
      return acc;
    }, {});

    updateUser.mutate({id, formData, token});
  };

  const handleCancel = () => {
    form.setFieldsValue(profileDetails);
    setIsEditing(false);
  };

  const handleDeleteProfile = async () => {
    if (!id) {
      toast.error('Missing user id, cannot delete profile');
      return;
    }

    try {
      setDeleteLoading(true);

      await axios.delete(
        `${AppConfig.API_BASE_URL}/api/user/delete/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toast.success('Profile deleted successfully!');
      dispatch(logoutStart());
      navigate('/');
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Profile deletion failed',
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-screen'}>
        <Spin size={'large'} />
      </div>
    );
  }

  if (!loggedIn && !token) {
    navigate('/');
    return null;
  }

  if (error) {
    return (
      <div className={'flex justify-center items-center h-screen p-4'}>
        <Alert
          message={'Failed to load profile'}
          description={'Unable to fetch your profile data. Please try again later.'}
          type={'error'}
          showIcon
        />
      </div>
    );
  }

  const roleConfig = ROLE_CONFIG[role] || {
    label: role || 'User',
    color: 'default',
  };

  const initials = (profileDetails?.fullName || '')
    .trim()
    .split(/\s+/)
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className={'relative min-h-screen bg-gray-50'}>
      <div className={'max-w-5xl mx-auto md:px-4 py-8 lg:py-12'}>
        <div
          className={
            'bg-primary-900 rounded-md p-6 mb-6 flex flex-col sm:flex-row items-center sm:items-end gap-4 text-white'
          }
        >
          <Avatar
            size={80}
            className={
              'bg-white text-primary-800 text-2xl font-bold flex-shrink-0'
            }
            style={{color: '#1e3a5f', fontWeight: 700}}
          >
            {initials || <UserOutlined />}
          </Avatar>

          <div className={'flex-1 text-center sm:text-left'}>
            <h1
              className={
                'text-2xl font-bold text-white capitalize m-0'
              }
            >
              {profileDetails?.fullName || 'Your Profile'}
            </h1>

            <p className={'text-primary-200 text-sm mt-1 m-0'}>
              {profileDetails?.email}
            </p>

            <div
              className={
                'flex flex-wrap justify-center sm:justify-start gap-2 mt-2'
              }
            >
              <Tag color={roleConfig.color} className={'capitalize'}>
                {roleConfig.label}
              </Tag>

              {profileDetails?.status && (
                <Tag
                  color={STATUS_COLOR[profileDetails.status] ?? 'default'}
                >
                  {profileDetails.status}
                </Tag>
              )}
            </div>
          </div>

          <div className={'flex gap-2 flex-shrink-0'}>
            <Button
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className={
                'bg-red-500 hover:!bg-red-600 border-red-500 hover:!border-red-600 text-white hover:!text-white'
              }
            >
              Log Out
            </Button>

            <Popconfirm
              title={'Delete Profile'}
              description={
                'Are you sure you want to permanently delete your profile?'
              }
              okText={'Delete'}
              cancelText={'Cancel'}
              okButtonProps={{
                danger: true,
                loading: deleteLoading,
              }}
              onConfirm={handleDeleteProfile}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoading}
                className={
                  'bg-white hover:!bg-red-50 border-white hover:!border-red-500 text-red-500 hover:!text-red-600'
                }
              >
                Delete Profile
              </Button>
            </Popconfirm>
          </div>
        </div>

        <div
          className={
            'bg-primary-50 rounded-md shadow-sm border border-primary-200 overflow-hidden'
          }
        >
          <div
            className={
              'flex md:flex-row flex-col items-center justify-between px-6 py-4 border-b border-gray-100'
            }
          >
            <div className={'flex items-center gap-2'}>
              <IdcardOutlined className={'text-primary-700 text-lg'} />
              <span className={'header'}>Personal Information</span>
            </div>

            {!isEditing ? (
              <Button
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
                className={'btn-primary'}
              >
                Edit Profile
              </Button>
            ) : (
              <div className={'flex gap-2'}>
                <Button icon={<CloseOutlined />} onClick={handleCancel}>
                  Cancel
                </Button>

                <Button
                  type={'primary'}
                  icon={<SaveOutlined />}
                  onClick={() => form.submit()}
                  loading={updateUser.isPending}
                  className={'btn-primary py-4'}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>

          <div className={'p-6'}>
            <Form form={form} layout={'vertical'} onFinish={onFinish}>
              <div className={'mb-2'}>
                <p
                  className={
                    'text-xs font-semibold text-primary-400 uppercase tracking-wider mb-3 flex items-center gap-1'
                  }
                >
                  <UserOutlined /> Personal Details
                </p>

                <div
                  className={
                    'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1'
                  }
                >
                  <Form.Item
                    name={'fullName'}
                    label={'Full Name'}
                    rules={[
                      {
                        required: true,
                        message: 'Full name is required',
                      },
                    ]}
                  >
                    <Input
                      className={'!text-primary-700'}
                      disabled={!isEditing}
                      placeholder={'Full name'}
                    />
                  </Form.Item>

                  <Form.Item
                    label={'Gender'}
                    name={'gender'}
                    rules={[
                      {
                        required: true,
                        message: 'Please select gender',
                      },
                    ]}
                  >
                    <Select
                      placeholder={'Select Gender'}
                      options={GENDER_OPTIONS}
                      disabled={!isEditing}
                    />
                  </Form.Item>

                  {role === 'PATIENT' && (
                    <Form.Item label={'Age'} name={'age'}>
                      <Input
                        className={'!text-primary-700'}
                        disabled={!isEditing}
                        placeholder={'32'}
                      />
                    </Form.Item>
                  )}
                </div>
              </div>

              <Divider className={'my-4'} />

              <div className={'mb-2'}>
                <p
                  className={
                    'text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1'
                  }
                >
                  <PhoneOutlined /> Contact
                </p>

                <div
                  className={
                    'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1'
                  }
                >
                  <Form.Item
                    name={'mobileNumber'}
                    label={'Mobile Number'}
                    rules={[
                      {
                        required: true,
                        message: 'Enter contact number',
                      },
                      {
                        pattern: /^[0-9]{10}$/,
                        message: 'Must be exactly 10 digits',
                      },
                    ]}
                  >
                    <Input
                      className={'!text-primary-700'}
                      disabled={!isEditing}
                      placeholder={'10-digit mobile number'}
                      maxLength={10}
                      prefix={
                        <PhoneOutlined className={'text-gray-300'} />
                      }
                    />
                  </Form.Item>

                  <Form.Item name={'email'} label={'Email'}>
                    <Input
                      disabled={!isEditing}
                      className={'!text-primary-700'}
                      placeholder={'Email address'}
                      prefix={
                        <MailOutlined className={'text-gray-300'} />
                      }
                    />
                  </Form.Item>
                </div>
              </div>

              <Divider className={'my-4'} />

              <div className={'mb-2'}>
                <p
                  className={
                    'text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1'
                  }
                >
                  <EnvironmentOutlined /> Address
                </p>

                <Form.Item name={'address'} label={'Full Address'}>
                  <Input.TextArea
                    disabled={!isEditing}
                    className={'!text-primary-700'}
                    rows={3}
                    placeholder={'Full address'}
                  />
                </Form.Item>
              </div>

              {(role === 'PATIENT'
                ? PATIENT_FIELDS.filter(f => f !== 'age')
                : extraFields
              ).length > 0 && (
                <>
                  <Divider className={'my-4'} />

                  <div className={'mb-2'}>
                    <p
                      className={
                        'text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1'
                      }
                    >
                      <IdcardOutlined />{' '}
                      {role === 'PATIENT'
                        ? 'Medical Details'
                        : 'Professional Details'}
                    </p>

                    <div
                      className={
                        'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1'
                      }
                    >
                      {role === 'PATIENT' ? (
                        <>
                          <Form.Item
                            label={'Blood Group'}
                            name={'bloodGroup'}
                          >
                            <Select
                              placeholder={'Select Blood Group'}
                              allowClear
                              disabled={!isEditing}
                              options={BLOOD_GROUP_OPTIONS.map(item => ({
                                value: item,
                                label: item,
                              }))}
                            />
                          </Form.Item>

                          <Form.Item
                            label={'Occupation'}
                            name={'occupation'}
                          >
                            <Input
                              className={'!text-primary-700'}
                              disabled={!isEditing}
                              placeholder={'Engineer'}
                            />
                          </Form.Item>

                          <Form.Item
                            label={'Marital Status'}
                            name={'maritalStatus'}
                          >
                            <Select
                              placeholder={'Select Marital Status'}
                              allowClear
                              disabled={!isEditing}
                              options={MARITAL_STATUS_OPTIONS.map(item => ({
                                value: item,
                                label: item,
                              }))}
                            />
                          </Form.Item>
                        </>
                      ) : (
                        extraFields.map(fieldKey => {
                          const def = FIELD_DEFS[fieldKey];

                          return (
                            <Form.Item
                              key={fieldKey}
                              label={def.label}
                              name={fieldKey}
                            >
                              <Input
                                className={'!text-primary-700'}
                                disabled={!isEditing}
                                placeholder={def.placeholder}
                              />
                            </Form.Item>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>
              )}

              {profileDetails?.createdOn && (
                <>
                  <Divider className={'my-4'} />

                  <div
                    className={
                      'grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1'
                    }
                  >
                    <div className={'text-sm text-primary-500'}>
                      <span className={'text-primary-400'}>
                        Member since:{' '}
                      </span>
                      {new Date(
                        profileDetails.createdOn,
                      ).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </div>
                  </div>
                </>
              )}
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
